import { Button, Dialog } from "@material-ui/core";
import AppBar from "components/app-bar";
import ErrorMessage from "components/error-message";
import SlideUpTransition from "components/slide-up-transition";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from "react-router";
import { StoreError, UserInfo } from "store/reducer";
import { TastingAnswer } from "store/reducers/answers/reducer";
import { TastingEvent } from "store/reducers/events/reducer";
import { events, answers, RootState } from '../../store';
import {
  Container,
  CancelButton,
  CircularProgress,
  UpdateButton,
  InnerContainer,
  RatingField,
  AsteriskedField,
  BeerSelectionField,
  ChangesMadeField,
  SecondaryBeerField,
  NotesField,
} from './TastingDetails.styles';
import useTastingRound from "./use-tasting-round";

const CONFIRM_MESSAGE = 'Discard changes made?';
type TastingDetailsParams = { id?: string, round?: string };

const TastingDetails = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { id = '', round } = useParams<TastingDetailsParams>();

  const roundIndex = round ? (parseInt(round) - 1) : -1;

  const user = useSelector<RootState, UserInfo | null>(s => s?.auth?.user);
  const event = useSelector<RootState, TastingEvent | undefined>(
    s => s?.events?.items?.find(e => e.id === id)
  );
  const eventLoading = useSelector<RootState, boolean>(s => s?.events?.itemsLoading[id]);
  const eventError = useSelector<RootState, StoreError>(s => s?.events?.itemsError[id]);
  const answer = useSelector<RootState, TastingAnswer | undefined>(
    (s) => s?.answers?.itemsById[id]
  );
  const answerLoading = useSelector<RootState, boolean>(
    (s) => Boolean(s?.answers?.loadingFor[id])
  );
  const answerLoadError = useSelector<RootState, StoreError | null>(
    (s) => s?.answers?.errorFor?.[id]
  );
  const updating = useSelector<RootState, boolean>(s => s?.answers?.update.updating);
  const updated = useSelector<RootState, TastingAnswer | null>(s => s?.answers?.update.updated);
  const answerError = useSelector<RootState, StoreError | null>(s => s?.answers?.update.error);

  // useTastingRound owns every form-field derivation, controlled state, and
  // handler. The component below stays focused on layout + dispatching.
  const form = useTastingRound({ event, answer, roundIndex });

  const [open, setOpen] = useState<boolean>(true);

  const loading = (eventLoading && !event) || (answerLoading && !answer);
  const loadError = eventError || answerLoadError;
  const error = loadError || answerError;
  const isBartender = (event?.bartender && (event.bartender === user?.email));

  //Load the event (if needed)
  useEffect(() => {
    dispatch(events.loadItem(id));
    dispatch(answers.loadItem(id));
  }, [dispatch, id]);

  //Go back once updated.
  useEffect(() => {
    if (updated) {
      dispatch(answers.resetUpdate());
      setOpen(false);
    }
  }, [updated, history, dispatch, id]);

  const handleCancel = () => {
    if (form.changes && !window.confirm(CONFIRM_MESSAGE)) {
      return;
    }
    dispatch(events.resetUpdate());
    setOpen(false);
  };

  const handleExit = () => {
    history.push(`/event/${id}/tasting`);
  };

  const handleSave = () => {
    if (!event || !answer) return;
    dispatch(answers.update(id, form.buildSavePayload()));
  };

  return (
    <Container id="tasting-details">
      <Dialog
        fullScreen
        open={open}
        onClose={handleCancel}
        TransitionProps={{ onExited: handleExit }}
        TransitionComponent={SlideUpTransition}
        scroll="paper"
      >
        <AppBar
          renderLeftComponent={() => <CancelButton onClick={handleCancel} />}
          title={`Round ${round}`}
          renderRightComponent={() => event && answer ? (
            <UpdateButton isSaving={updating} onClick={handleSave} />
          ) : null}
        />
        <InnerContainer>
          {error && (
            <ErrorMessage error={error}>
              {loadError && (
                <Button color="inherit" onClick={() => {
                  if (eventError) dispatch(events.loadItem(id));
                  if (answerLoadError) dispatch(answers.loadItem(id));
                }}>
                  Retry
                </Button>
              )}
            </ErrorMessage>
          )}
          {loading ? (
            <CircularProgress />
          ) : event && answer ? (
            <>
              <BeerSelectionField
                beers={event?.beers as any[]}
                beerPreselectedIndex={form.beerPreselectedIndex}
                isBeerSelected={form.isBeerSelected}
                onClickBeer={form.handleClickBeer}
              />
              {!isBartender && (
                <>
                  <RatingField
                    rating={form.selectedRating}
                    onChange={form.handleRatingChange}
                  />
                  <AsteriskedField
                    selected={form.selectedAsterisked}
                    onChange={form.handleAsteriskedChange}
                    disabled={!form.selectedAsterisked && form.asterisksLeft() <= 0}
                    asterisksLeft={form.asterisksLeft()}
                    showDouble={form.eventAllowsDouble && form.selectedAsterisked}
                    doubleSelected={form.selectedDoubleAsterisked}
                    doubleDisabled={form.doubleDisabled}
                    onDoubleChange={form.handleDoubleAsteriskedChange}
                  />
                  {form.showReminder && (
                    <SecondaryBeerField
                      beers={event?.beers ?? []}
                      selectedBeer={form.selectedSecondaryBeer}
                      primaryBeer={form.selectedBeer}
                      onChange={form.setSelectedSecondaryBeer}
                    />
                  )}
                  {form.showNotes && (
                    <NotesField
                      value={form.selectedNotes}
                      onChange={form.setSelectedNotes}
                    />
                  )}
                  <ChangesMadeField changesMade={form.changesMade} />
                </>
              )}
            </>
          ) : null}
        </InnerContainer>
      </Dialog>
    </Container>
  );
};
export default TastingDetails;
